import { Component } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { postMessage } from '../../utils/common.utils';
import { withTranslation } from '../../hoc/withTranslation';
import PreLoginAuthSvg from '../../assets/PreLoginAuthSvg';
import TrustMarker from '../common/TrustMarker';
import loginLeftStyles from '../Login/LoginLeft.styles';
import loginStyles from '../Login/Login.styles';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';
import LoginFooter from '../Sections/LoginFooter';
import Footer from '../Sections/Footer';
import Drawer from '../Drawer/Drawer';
import DialogBox from '../Dialog Box/Dialog Box';
import CancelConsent from '../Accounts/CancelConsent';
import SetTheme from '../common/SetTheme';
import {
	PAGE_HEADING_ID,
	PRE_LOGIN_CANCEL_DRAWER_SUBTITLE_ID,
	PRE_LOGIN_CANCEL_DRAWER_TITLE_ID,
} from '../../utils/pageHeading.utils';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';
import PinSetupFlow from './PinSetupFlow';
import {
	grantMfaEntry,
	isMfaEntryGranted,
	revokeMfaEntry,
} from '../../utils/authRouteGuard.utils';
import { is2FAEnabled } from '../../utils/entityConfig.utils';
import {
	captureMfaFactorChooserViewed,
	captureMfaFactorSelected,
	captureMfaWebAuthnCeremonyStarted,
	captureMfaWebAuthnCompleted,
	captureMfaWebAuthnFailed,
	captureMfaWebAuthnFallbackToPin,
} from '../../utils/analytics/posthog.utils';
import {
	initMfaWsSniffer,
	getLastMfaWsError,
	clearLastMfaWsError,
	getMfaWsFactorData,
	isMfaFactorEnrolled,
} from '../../utils/mfaWsSniffer.utils';

initMfaWsSniffer();

class TwoFaChoice extends Component {
	constructor(props) {
		super(props);
		this.state = {
			theme: undefined,
			showCancelDialog: false,
			showCancelDrawer: false,
			showPinSetup: false,
			headerHeight: 70,
			passkeyFailed: false,
			passkeyError: '',
			isPinRegistered: undefined,
		};
		this.calculateHeaderHeight = () => {
			const appBar = document.querySelector('header.MuiAppBar-root');
			if (!appBar) return;
			const height = Math.ceil(appBar.getBoundingClientRect().height);
			if (height !== this.state.headerHeight) {
				this.setState({ headerHeight: height });
			}

			appBar.querySelectorAll('img').forEach((img) => {
				if (!img.complete) {
					img.addEventListener('load', this.calculateHeaderHeight, {
						once: true,
					});
				}
			});
		};
	}

	ensureMfaEntryFromAuth = () => {
		if (!is2FAEnabled()) {
			console.warn('[TwoFaChoice] enable2FA is not enabled. Redirecting to /login');
			this.props.history.replace({
				pathname: '/login',
				search: window.location.search,
			});
			return false;
		}
		if (this._mfaEntryGranted) {
			return true;
		}
		const { authVerified } = this.props.location?.state || {};
		if (!authVerified && !isMfaEntryGranted()) {
			this.props.history.replace({
				pathname: '/',
				search: window.location.search,
			});
			return false;
		}
		grantMfaEntry();
		this._mfaEntryGranted = true;
		return true;
	};

	getJourneyFlags = () => {
		const wsFactorData = getMfaWsFactorData();
		const wsEnrolled = isMfaFactorEnrolled();
		const step = window._currentMfaStep;

		const factorEnrolled =
			this.state?.isPinRegistered !== undefined
				? this.state.isPinRegistered
				: step?.factorData?.enrolled !== undefined
					? step.factorData.enrolled
					: step?.inputSpec?.enrolled !== undefined
						? step.inputSpec.enrolled
						: wsEnrolled !== undefined
							? wsEnrolled
							: wsFactorData?.enrolled !== undefined
								? wsFactorData.enrolled
								: step?.requirement === 'verification'
									? true
									: step?.requirement === 'enrollment'
										? false
										: undefined;

		const isPinRegistered = Boolean(
			factorEnrolled !== undefined
				? factorEnrolled
				: global.isPinRegistered ||
				  this.props.location?.state?.isPinRegistered,
		);

		const isLoginJourney = Boolean(
			global.journeyType === 'login' ||
			global.isReturningUser ||
			this.props.location?.state?.journey === 'login' ||
			this.props.location?.state?.isReturningUser ||
			global.newUser === 'N' ||
			global.newUser === false,
		);

		return { isPinRegistered, isLoginJourney };
	};

	componentDidMount() {
		if (!this.ensureMfaEntryFromAuth()) {
			return;
		}

		const { isPinRegistered, isLoginJourney } = this.getJourneyFlags();
		const available = window._currentMfaStep?.availableFactors || ['webauthn', 'pin'];
		captureMfaFactorChooserViewed({
			availableFactors: available,
			journey: isLoginJourney ? 'login' : 'signup',
			isReturningUser: isLoginJourney,
			isPinRegistered,
		});

		if (
			window._currentMfaStep &&
			typeof window._currentMfaStep.selectFactor === 'function'
		) {
			this._selectFactorStep = window._currentMfaStep;
		}

		this.calculateHeaderHeight();
		window.addEventListener('resize', this.calculateHeaderHeight);
		window.addEventListener('load', this.calculateHeaderHeight);
		window.addEventListener('finvu:openExitConfirm', this.openExitConfirm);
		requestAnimationFrame(() => this.calculateHeaderHeight());

		const appBar = document.querySelector('header.MuiAppBar-root');
		if (appBar) {
			this.headerObserver = new MutationObserver(this.calculateHeaderHeight);
			this.headerObserver.observe(appBar, {
				childList: true,
				subtree: true,
				attributes: true,
			});
		}
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.location?.state?.authVerified !==
			this.props.location?.state?.authVerified
		) {
			if (!this.ensureMfaEntryFromAuth()) {
				return;
			}
		}

		requestAnimationFrame(() => this.calculateHeaderHeight());
	}

	componentWillUnmount() {
		if (this._toastTimer) {
			clearTimeout(this._toastTimer);
			this._toastTimer = null;
		}
		window.removeEventListener('resize', this.calculateHeaderHeight);
		window.removeEventListener('load', this.calculateHeaderHeight);
		window.removeEventListener('finvu:openExitConfirm', this.openExitConfirm);
		this.headerObserver?.disconnect();
	}

	setPasskeyFailed = (passkeyError, persist = true) => {
		if (this._toastTimer) {
			clearTimeout(this._toastTimer);
			this._toastTimer = null;
		}
		this.setState({ passkeyFailed: true, passkeyError });
		if (!persist) {
			this._toastTimer = setTimeout(() => {
				this.setState({ passkeyFailed: false, passkeyError: '' });
			}, 3000);
		}
	};

	openExitConfirm = () => {
		const isMobile = window.innerWidth < 900;
		this.setState(
			isMobile ? { showCancelDrawer: true } : { showCancelDialog: true },
		);
	};

	handleBack = () => {
		postMessage('exit', 'BACK');
		if (global.isRedirect && global.redirectUrl?.includes('?')) {
			window.location.replace(
				`${global.redirectUrl}&ecres=${global.encryptedres}&resdate=${global.resDate}&fi=${global.encryptedFiId}`,
			);
		} else if (global.isRedirect) {
			window.location.replace(
				`${global.redirectUrl}?ecres=${global.encryptedres}&resdate=${global.resDate}&fi=${global.encryptedFiId}`,
			);
		}
	};

	handleEnablePasskey = async () => {
		console.log('[TwoFaChoice] User clicked Enable / Retry Passkey');
		const { isLoginJourney } = this.getJourneyFlags();
		const passkeyStartTime = performance.now();
		captureMfaFactorSelected({
			chosenFactor: 'webauthn',
			interactionType: 'primary_cta',
		});
		captureMfaWebAuthnCeremonyStarted({
			journey: isLoginJourney ? 'login' : 'signup',
		});

		this.setState({
			isPasskeyLoading: true,
			passkeyFailed: false,
			passkeyError: '',
		});
		try {
			let chooser =
				window._currentMfaStep &&
				typeof window._currentMfaStep.selectFactor === 'function'
					? window._currentMfaStep
					: this._selectFactorStep;

			// If active chooser is missing or stale, refresh via mfaClient.login
			if (!chooser || typeof chooser.selectFactor !== 'function') {
				if (window.mfaClient && typeof window.mfaClient.login === 'function') {
					console.log(
						'[TwoFaChoice] Refreshing step via mfaClient.login for retry...',
					);
					const rawSrc =
						global.consentHandle ||
						global.srcRef ||
						global.src_ref ||
						global.consentIdReference;
					const singleSrc = Array.isArray(rawSrc)
						? rawSrc[0]
						: typeof rawSrc === 'string' && rawSrc.includes(',')
							? rawSrc.split(',')[0].trim()
							: rawSrc;

					const freshStep = await window.mfaClient.login({
						mobileNumber: global.mobileNumber,
						handleId:
							global.handleId ||
							(global.mobileNumber
								? `${global.mobileNumber}@finvu`
								: undefined),
						consentHandle: singleSrc,
					});
					if (freshStep) {
						window._currentMfaStep = freshStep;
						if (typeof freshStep.selectFactor === 'function') {
							this._selectFactorStep = freshStep;
							chooser = freshStep;
						}
					}
				}
			}

			if (chooser && typeof chooser.selectFactor === 'function') {
				this._selectFactorStep = chooser;
				console.log('[TwoFaChoice] Selecting webauthn on active chooser...');
				const nextStep = await chooser.selectFactor('webauthn');
				console.log(
					'[TwoFaChoice] Step after selectFactor(webauthn):',
					nextStep,
				);
				window._currentMfaStep = nextStep;
				if (nextStep?.action === 'silent' && nextStep.completion) {
					console.log('[TwoFaChoice] Awaiting WebAuthn completion promise...');
					const finalStep = await nextStep.completion;
					console.log(
						'[TwoFaChoice] WebAuthn completion resolved to:',
						finalStep,
					);
					window._currentMfaStep = finalStep;
					if (
						finalStep?.action === 'complete' &&
						finalStep.result?.status === 'authenticated'
					) {
						captureMfaWebAuthnCompleted({
							durationMs: Math.round(performance.now() - passkeyStartTime),
							journey: isLoginJourney ? 'login' : 'signup',
						});
						this.continueAfterMfaChoice();
						return;
					} else if (
						finalStep?.action === 'input' &&
						finalStep.factor === 'pin'
					) {
						console.log('[TwoFaChoice] WebAuthn resolved to PIN fallback.');
						const wsFactorData = getMfaWsFactorData();
						const wsEnrolled = isMfaFactorEnrolled();
						const isEnrolled =
							finalStep?.factorData?.enrolled === true ||
							finalStep?.inputSpec?.enrolled === true ||
							wsEnrolled === true ||
							wsFactorData?.enrolled === true ||
							finalStep?.requirement === 'verification';
						const errText = isEnrolled
							? this.props.t('twoFaChoice.passkeyVerifyFailed') ||
								'Passkey authentication failed. Please enter your PIN.'
							: this.props.t('twoFaChoice.passkeyFailed') ||
								'Passkey creation failed, please try creating a PIN';

						captureMfaWebAuthnFallbackToPin({
							durationMs: Math.round(performance.now() - passkeyStartTime),
							error: errText,
							fallbackFactor: 'pin',
						});

						const available = Array.isArray(finalStep.availableFactors)
							? finalStep.availableFactors
							: [];
						const webauthnAllowed = available.includes('webauthn');

						this.setState({
							fallbackToPin: !webauthnAllowed,
							expectedFactor: 'PIN',
							isPinRegistered: isEnrolled,
						});
						this.setPasskeyFailed(errText);
						return;
					} else if (
						finalStep?.action === 'complete' &&
						finalStep.result?.status === 'failed'
					) {
						const errText =
							finalStep.result.error?.message ||
							this.props.t('twoFaChoice.passkeyFailed') ||
							'Passkey creation failed, please try creating a PIN';
						captureMfaWebAuthnFailed({
							durationMs: Math.round(performance.now() - passkeyStartTime),
							errorCode: finalStep.result.error?.code,
							errorMessage: errText,
						});
						this.setPasskeyFailed(errText);
						return;
					}
				}
			} else if (
				window._currentMfaStep &&
				window._currentMfaStep.action === 'complete' &&
				window._currentMfaStep.result?.status === 'authenticated'
			) {
				captureMfaWebAuthnCompleted({
					durationMs: Math.round(performance.now() - passkeyStartTime),
					journey: isLoginJourney ? 'login' : 'signup',
				});
				this.continueAfterMfaChoice();
				return;
			}
		} catch (err) {
			console.error('[TwoFaChoice] Passkey activation failed:', err);
			captureMfaWebAuthnFailed({
				durationMs: Math.round(performance.now() - passkeyStartTime),
				errorCode: err?.code || err?.errorCode,
				errorMessage: err?.message,
			});
			const isNotAllowed =
				err?.code === 'FACTOR_NOT_ALLOWED' ||
				err?.errorCode === 'FACTOR_NOT_ALLOWED' ||
				err?.message?.includes('Cannot switch back') ||
				err?.message?.includes('earlier step-up factor');

			if (isNotAllowed) {
				this.setState({ fallbackToPin: true, expectedFactor: 'PIN' });
			}
			this.setPasskeyFailed(
				err?.message || 'Passkey creation failed, please try creating a PIN',
			);
			return;
		} finally {
			this.setState({ isPasskeyLoading: false });
		}
	};

	handleCreatePin = async () => {
		console.log('[TwoFaChoice] User clicked Create/Enter PIN');
		const { isPinRegistered } = this.getJourneyFlags();
		captureMfaFactorSelected({
			chosenFactor: 'pin',
			interactionType: isPinRegistered ? 'secondary_cta' : 'primary_cta',
		});
		this.setState({
			isPasskeyLoading: true,
			fallbackToPin: true,
			expectedFactor: 'PIN',
		});
		clearLastMfaWsError();
		try {
			if (
				window._currentMfaStep &&
				typeof window._currentMfaStep.selectFactor === 'function'
			) {
				console.log(
					'[TwoFaChoice] Selecting PIN factor on active step in handleCreatePin...',
				);
				const pinStep = await window._currentMfaStep.selectFactor('pin');
				console.log('[TwoFaChoice] Step after selectFactor(pin):', pinStep);
				window._currentMfaStep = pinStep;

				if (
					pinStep?.action === 'complete' &&
					pinStep.result?.status === 'failed'
				) {
					const errorMsg =
						getLastMfaWsError() ||
						pinStep.result.error?.message ||
						'Failed to initialize PIN';
					console.error(
						'[TwoFaChoice] selectFactor(pin) returned failed step:',
						pinStep.result,
					);
					toast.error(errorMsg);
					this.setState({ isPasskeyLoading: false });
					return;
				}

				const wsFactorData = getMfaWsFactorData();
				const wsEnrolled = isMfaFactorEnrolled();

				const factorEnrolled =
					pinStep?.factorData?.enrolled !== undefined
						? pinStep.factorData.enrolled
						: pinStep?.inputSpec?.enrolled !== undefined
							? pinStep.inputSpec.enrolled
							: wsEnrolled !== undefined
								? wsEnrolled
								: wsFactorData?.enrolled !== undefined
									? wsFactorData.enrolled
									: undefined;

				const isEnrolled =
					factorEnrolled === true ||
					pinStep?.requirement === 'verification';
				const isEnrollment =
					factorEnrolled === false ||
					pinStep?.requirement === 'enrollment';

				const updatedIsPinRegistered = isEnrollment
					? false
					: isEnrolled
						? true
						: (this.state.isPinRegistered ?? isPinRegistered);

				if (updatedIsPinRegistered) {
					global.isPinRegistered = true;
				}

				this.setState({
					isPasskeyLoading: false,
					showPinSetup: true,
					isPinRegistered: updatedIsPinRegistered,
				});
				return;
			}
			this.setState({ isPasskeyLoading: false, showPinSetup: true });
		} catch (err) {
			console.warn('[TwoFaChoice] selectFactor(pin) error:', err);
			this.setState({ isPasskeyLoading: false, showPinSetup: true });
		}
	};

	submitPin = async (pin) => {
		console.log(
			'[TwoFaChoice] submitPin called (length: ' + (pin ? pin.length : 0) + ')',
		);
		clearLastMfaWsError();
		try {
			if (
				window._currentMfaStep &&
				typeof window._currentMfaStep.selectFactor === 'function'
			) {
				console.log('[TwoFaChoice] Selecting PIN factor on active step...');
				const pinStep = await window._currentMfaStep.selectFactor('pin');
				console.log('[TwoFaChoice] Step after selectFactor(pin):', pinStep);
				window._currentMfaStep = pinStep;
				if (
					pinStep?.action === 'input' &&
					typeof pinStep.submit === 'function'
				) {
					console.log('[TwoFaChoice] Submitting PIN to active step...');
					const nextStep = await pinStep.submit(pin);
					console.log('[TwoFaChoice] Step after PIN submit:', nextStep);
					window._currentMfaStep = nextStep;
					if (nextStep?.action === 'input' && nextStep.validationError) {
						console.warn(
							'[TwoFaChoice] PIN validation error:',
							nextStep.validationError,
						);
						return false;
					}
					if (nextStep?.action === 'complete') {
						if (nextStep.result?.status === 'authenticated') {
							return true;
						}
						console.warn(
							'[TwoFaChoice] PIN submit returned failed completion:',
							nextStep.result,
						);
						return false;
					}
					return true;
				}
			} else if (
				window._currentMfaStep &&
				typeof window._currentMfaStep.submit === 'function'
			) {
				console.log('[TwoFaChoice] Submitting PIN to current input step...');
				const nextStep = await window._currentMfaStep.submit(pin);
				console.log('[TwoFaChoice] Step after PIN submit:', nextStep);
				window._currentMfaStep = nextStep;
				if (nextStep?.action === 'input' && nextStep.validationError) {
					console.warn(
						'[TwoFaChoice] PIN validation error:',
						nextStep.validationError,
					);
					return false;
				}
				if (nextStep?.action === 'complete') {
					if (nextStep.result?.status === 'authenticated') {
						return true;
					}
					console.warn(
						'[TwoFaChoice] PIN submit returned failed completion:',
						nextStep.result,
					);
					return false;
				}
				return true;
			}
			if (
				window.mfaClient &&
				typeof window.mfaClient.enrollPin === 'function'
			) {
				console.log(
					'[TwoFaChoice] Enrolling PIN via window.mfaClient.enrollPin...',
				);
				await window.mfaClient.enrollPin(pin);
				if (typeof window.mfaClient.verifyPin === 'function') {
					console.log(
						'[TwoFaChoice] Verifying PIN via window.mfaClient.verifyPin...',
					);
					await window.mfaClient.verifyPin(pin);
				}
				return true;
			}
			if (window.finvuClient?.setUserPin) {
				console.log(
					'[TwoFaChoice] Setting PIN via window.finvuClient.setUserPin...',
				);
				await window.finvuClient.setUserPin(pin);
			}
			return true;
		} catch (e) {
			console.error('[TwoFaChoice] submitPin error:', e);
			return false;
		}
	};

	handlePinSetupClose = () => {
		this.setState({ showPinSetup: false });
	};

	handlePinSetupSuccess = () => {
		this.setState({ showPinSetup: false });
		this.continueAfterMfaChoice();
	};

	continueAfterMfaChoice = () => {
		const step = window._currentMfaStep;
		if (
			step &&
			(step.action !== 'complete' || step.result?.status !== 'authenticated')
		) {
			console.warn(
				'[TwoFaChoice] Navigation blocked: User is not authenticated yet.',
				step,
			);
			return;
		}
		revokeMfaEntry();
		this.props.history.replace({
			pathname: '/login',
			search: window.location.search,
			state: { continueAfterMfa: true },
		});
	};

	setTheme = (theme) => {
		this.setState({ theme });
	};

	render() {
		if (!this.props.sdkReady) {
			return (
				<Box sx={{ display: 'flex', p: 2 }}>
					<CircularProgress />
				</Box>
			);
		}

		const { t } = this.props;
		const isDark =
			this.props.customDetails?.darkTheme === true ||
			this.state.theme?.palette?.mode === 'dark';
		const isMobile = window.innerWidth < 1200;
		const primaryColor = this.props.customDetails?.primaryColor || '#3053D3';

		// Detect biometric label: Android -> fingerprint, iOS -> Face ID, Desktop -> passkey (generic)
		const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
		const isAndroid = /android/i.test(ua);
		const isIOS = /iphone|ipad|ipod/i.test(ua);
		const isDesktop = !isAndroid && !isIOS;

		const biometricLabel = isAndroid
			? t('twoFaChoice.biometricFingerprint') || 'fingerprint'
			: isIOS
				? t('twoFaChoice.biometricFaceId') || 'Face ID'
				: t('twoFaChoice.biometricPasskey') || 'passkey';

		const biometricLabelCap = isAndroid
			? 'Fingerprint'
			: isIOS
				? 'Face ID'
				: 'Passkey';

		const hasFailed = Boolean(this.state.passkeyFailed);

		// Response-driven check: Is WebAuthn still permitted?
		const step = window._currentMfaStep;
		const fallbackFactors =
			step?.fallbackStepUpFactors ||
			step?.fallBackStepUpFactors ||
			step?.rawResponse?.payload?.fallbackStepUpFactors ||
			step?.rawResponse?.payload?.fallBackStepUpFactors ||
			this.state.fallbackStepUpFactors ||
			[];

		const normalizedFallbackFactors = (
			Array.isArray(fallbackFactors) ? fallbackFactors : []
		).map((f) => String(f).toLowerCase());

		// If webauthn is included in either availableFactors or fallbackStepUpFactors, it is permitted
		const hasWebauthn = Boolean(
			(Array.isArray(step?.availableFactors) &&
				step.availableFactors.includes('webauthn')) ||
			normalizedFallbackFactors.includes('webauthn'),
		);

		const isStepLockedToPin = Boolean(
			!hasWebauthn &&
			((step && step.action === 'input' && step.factor === 'pin') ||
				this.state.fallbackToPin ||
				this.state.expectedFactor === 'PIN' ||
				(normalizedFallbackFactors.includes('pin') && hasFailed)),
		);

		const canUseWebauthn = !isStepLockedToPin;

		const { isPinRegistered, isLoginJourney } = this.getJourneyFlags();

		const biometricTitle = hasFailed
			? t('twoFaChoice.signInFailedTitle') || 'We couldn’t sign you in'
			: isLoginJourney
				? t('twoFaChoice.signInTitle', { biometricLabel: biometricLabelCap }) ||
					`Sign in with ${biometricLabelCap}`
				: t('twoFaChoice.title', { biometricLabel }) ||
					`Setup ${biometricLabel} for login`;

		const biometricDescription = hasFailed
			? !canUseWebauthn
				? isDesktop
					? t('twoFaChoice.passkeySignInFailedPinOnlyDescription') ||
						'Passkey sign-in didn’t work. Please use your Finvu PIN to continue.'
					: t('twoFaChoice.signInFailedPinOnlyDescription', {
							biometricLabel,
						}) ||
						`${biometricLabel} didn’t work. Please use your Finvu PIN to continue.`
				: isDesktop
					? t('twoFaChoice.passkeySignInFailedDescription') ||
						'Passkey sign-in didn’t work. Try your passkey again or use your Finvu PIN to continue.'
					: t('twoFaChoice.signInFailedDescription', { biometricLabel }) ||
						`${biometricLabel} didn’t work. Try ${biometricLabel} again or use your Finvu PIN to continue.`
			: isLoginJourney
				? t('twoFaChoice.signInDescription', { biometricLabel }) ||
					`Use ${biometricLabel} or device lock instead of using a PIN to sign in`
				: t('twoFaChoice.description', { biometricLabel }) ||
					`Passkey is an easier and more secure way to sign in. Use ${biometricLabel} or device lock instead of using a PIN`;

		const primaryButtonText = !canUseWebauthn
			? isPinRegistered
				? t('twoFaChoice.usePin') || 'Use PIN instead'
				: t('twoFaChoice.enterPin') || 'Enter PIN'
			: hasFailed
				? t('twoFaChoice.retryPasskey', { biometricLabel }) ||
					`Try ${biometricLabel} again`
				: isLoginJourney
					? t('twoFaChoice.signIn', { biometricLabel: biometricLabelCap }) ||
						`Sign in with ${biometricLabelCap}`
					: t('twoFaChoice.enable') || 'Yes, enable';

		const secondaryButtonText = isPinRegistered
			? t('twoFaChoice.usePin') || 'Use PIN instead'
			: t('twoFaChoice.createPin') || 'No, create PIN instead';

		const showSecondaryButton = canUseWebauthn;
		const primaryAction = canUseWebauthn
			? this.handleEnablePasskey
			: this.handleCreatePin;
		const showTrustMarker =
			this.props.customDetails?.disableTrustMarkers !== 'true';
		const secondaryButtonBg = isDark
			? 'rgba(255, 255, 255, 0.08)'
			: `color-mix(in srgb, ${primaryColor} 12%, #ffffff)`;

		const primaryButtonSx = getEntityThemedCtaButtonSx(
			this.props.customDetails,
			isDark,
			{
				textTransform: 'none',
				fontSize: '0.9375rem',
				fontWeight: 600,
				border: `1px solid ${this.props.customDetails?.ctaBorderColor ?? primaryColor}`,
				borderRadius: this.props.customDetails?.borderRadius ?? '6px',
				padding: '12px 0',
				boxShadow: 'none',
				fontFamily: this.props.customDetails?.fontFamily,
				...keyboardFocusVisibleSx,
				'&:hover': { boxShadow: 'none' },
			},
		);

		const secondaryButtonSx = {
			backgroundColor: secondaryButtonBg,
			textTransform: 'capitalize',
			fontSize: '0.875rem',
			fontWeight: 600,
			border: 'none',
			borderRadius: this.props.customDetails?.borderRadius ?? '6px',
			boxShadow: 'none',
			color: primaryColor,
			fontFamily: this.props.customDetails?.fontFamily,
			padding: '12px 0',
			...keyboardFocusVisibleSx,
			'&:hover': {
				backgroundColor: isDark
					? 'rgba(255, 255, 255, 0.12)'
					: `color-mix(in srgb, ${primaryColor} 18%, #ffffff)`,
				boxShadow: 'none',
			},
		};

		return (
			<Box
				sx={{
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					backgroundColor: isDark ? '#161616' : '#fff',
					boxSizing: 'border-box',
					pt: { xs: 0, md: `${this.state.headerHeight}px` },
				}}
			>
				{/* Mobile hero */}
				<Box
					sx={{
						display: { xs: 'block', md: 'none' },
						flexShrink: 0,
						backgroundColor: primaryColor,
						pt: `${this.state.headerHeight}px`,
					}}
				>
					<Box sx={{ px: 0, pb: 1 }}>
						<PreLoginAuthSvg primaryColor={primaryColor} />
					</Box>
				</Box>

				{showTrustMarker && (
					<Box
						sx={{
							width: '100%',
							flexShrink: 0,
							display: { xs: 'none', md: 'block' },
							'& > div': {
								padding: '12px 20px !important',
							},
						}}
					>
						<TrustMarker
							customDetails={this.props.customDetails}
							noBorderRadius
						/>
					</Box>
				)}

				<Box
					sx={{
						display: 'flex',
						flex: 1,
						minHeight: 0,
						overflow: 'hidden',
						paddingTop: 0,
					}}
				>
					<SetTheme
						customDetails={this.props.customDetails}
						setTheme={this.setTheme}
					/>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flex: { xs: 1, lg: 1 },
							minHeight: 0,
							minWidth: 0,
							backgroundColor: isDark ? '#161616' : '#fff',
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflowY: 'auto',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{showTrustMarker && (
								<Box
									sx={{
										flexShrink: 0,
										display: { xs: 'block', lg: 'none' },
									}}
								>
									<TrustMarker
										customDetails={this.props.customDetails}
										noBorderRadius
									/>
								</Box>
							)}

							<Box
								sx={{
									flex: 1,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: { xs: 'flex-start', lg: 'center' },
									width: '100%',
									maxWidth: {
										xs: '100%',
										sm: '480px',
										md: '560px',
										lg: '100%',
									},
									mx: 'auto',
									px: { xs: '1.5rem', lg: '4rem', xl: '5.5rem' },
									pt: { xs: '1.5rem', lg: '2rem' },
									pb: { xs: '1rem', lg: '2rem' },
									textAlign: 'center',
									minHeight: 0,
								}}
							>
								<Typography
									id={PAGE_HEADING_ID}
									component="h1"
									sx={{
										...loginStyles.headingText,
										lineHeight: 1.45,
										mb: '0.75rem',
										maxWidth: '28rem',
										fontFamily: this.props.customDetails?.fontFamily,
										color:
											this.props.customDetails?.primaryTextColor ??
											(isDark ? '#fff' : '#111'),
									}}
								>
									{biometricTitle}
								</Typography>
								<Typography
									component="p"
									sx={{
										...loginStyles.subHeading,
										lineHeight: 1.45,
										maxWidth: '24rem',
										mb: { xs: 0, lg: '2.5rem' },
										m: 0,
										display: 'block',
										flex: 'unset',
										alignItems: 'unset',
										fontFamily: this.props.customDetails?.fontFamily,
										color:
											this.props.customDetails?.secondaryTextColor ??
											(isDark ? '#B0B0B0' : '#6C6C6C'),
									}}
								>
									{biometricDescription}
								</Typography>

								<Box
									sx={{
										width: '100%',
										maxWidth: '24rem',
										display: { xs: 'none', lg: 'flex' },
										flexDirection: 'column',
										gap: '0.75rem',
									}}
								>
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={primaryAction}
										sx={primaryButtonSx}
									>
										{primaryButtonText}
									</Button>
									{showSecondaryButton && (
										<Button
											variant="contained"
											size="large"
											fullWidth
											onClick={this.handleCreatePin}
											sx={secondaryButtonSx}
										>
											{secondaryButtonText}
										</Button>
									)}
								</Box>
							</Box>
						</Box>

						<Box
							sx={{
								flexShrink: 0,
								display: { xs: 'block', lg: 'none' },
								width: '100%',
								maxWidth: { xs: '100%', sm: '480px', md: '560px', lg: '100%' },
								mx: 'auto',
								px: '1.5rem',
								pb: '1rem',
								backgroundColor: isDark ? '#161616' : '#fff',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '0.75rem',
								}}
							>
								<Button
									variant="contained"
									size="large"
									fullWidth
									onClick={primaryAction}
									sx={primaryButtonSx}
								>
									{primaryButtonText}
								</Button>
								{showSecondaryButton && (
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={this.handleCreatePin}
										sx={secondaryButtonSx}
									>
										{secondaryButtonText}
									</Button>
								)}
							</Box>
						</Box>

						{showTrustMarker && !isMobile && (
							<Box
								sx={{
									flexShrink: 0,
									px: { xs: '1.5rem', lg: '4rem', xl: '5.5rem' },
									pb: '1rem',
									backgroundColor: isDark ? '#161616' : '#fff',
								}}
							>
								<LoginFooter mode="secure" />
							</Box>
						)}
					</Box>

					{/* Desktop hero panel */}
					<Box
						sx={{
							display: { xs: 'none', lg: 'block' },
							flex: 2,
							minHeight: 0,
							minWidth: 0,
							alignSelf: 'stretch',
							overflow: 'hidden',
							backgroundColor: primaryColor,
							position: 'relative',
						}}
					>
						<PreLoginAuthSvg
							primaryColor={primaryColor}
							preserveAspectRatio="xMidYMid slice"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
							}}
						/>
					</Box>
				</Box>

				<Footer customDetails={this.props.customDetails} embedded />

				<DialogBox
					open={this.state.showCancelDialog}
					handleClose={() => this.setState({ showCancelDialog: false })}
					customDetails={this.props.customDetails}
				>
					<CancelConsent
						handleCancel={() => this.setState({ showCancelDialog: false })}
						handleDeny={this.handleBack}
						customDetails={this.props.customDetails}
					/>
				</DialogBox>

				<Drawer
					open={this.state.showCancelDrawer}
					setOpen={(open) => this.setState({ showCancelDrawer: open })}
					anchor="bottom"
					height="auto"
					customDetails={this.props.customDetails}
					showCloseBtn={false}
					ariaLabelledBy={PRE_LOGIN_CANCEL_DRAWER_TITLE_ID}
					ariaDescribedBy={PRE_LOGIN_CANCEL_DRAWER_SUBTITLE_ID}
				>
					<Box
						sx={{
							padding: '1.5rem',
							fontFamily: this.props.customDetails?.fontFamily,
							backgroundColor: isDark
								? '#161616'
								: this.props.customDetails?.secondaryColor || '#fff',
						}}
					>
						<Typography
							id={PRE_LOGIN_CANCEL_DRAWER_TITLE_ID}
							component="h2"
							sx={{
								fontSize: '1rem',
								fontWeight: 600,
								fontFamily: this.props.customDetails?.fontFamily,
								color: isDark ? '#fff' : '#000',
								marginBottom: '0.5rem',
							}}
						>
							{t('header.dialog.cancelConsent.title')}
						</Typography>
						<Typography
							id={PRE_LOGIN_CANCEL_DRAWER_SUBTITLE_ID}
							sx={{
								fontSize: '0.875rem',
								fontFamily: this.props.customDetails?.fontFamily,
								color: isDark ? '#B0B0B0' : '#666',
								marginBottom: '2rem',
							}}
						>
							{t('header.dialog.cancelConsent.subtitle')}
						</Typography>
						<CancelConsent
							handleCancel={() => this.setState({ showCancelDrawer: false })}
							handleDeny={this.handleBack}
							customDetails={this.props.customDetails}
						/>
					</Box>
					<Box sx={{ marginBottom: '-1rem' }}>
						<Footer {...this.props} />
					</Box>
				</Drawer>

				<PinSetupFlow
					open={this.state.showPinSetup}
					onClose={this.handlePinSetupClose}
					onSuccess={this.handlePinSetupSuccess}
					onSubmitPin={this.submitPin}
					customDetails={this.props.customDetails}
					isDark={isDark}
					isMobile={isMobile}
					parentProps={this.props}
					isPinRegistered={
						this.state.isPinRegistered !== undefined
							? this.state.isPinRegistered
							: isPinRegistered
					}
					mode={
						(this.state.isPinRegistered !== undefined
							? this.state.isPinRegistered
							: isPinRegistered)
							? 'enter'
							: 'setup'
					}
				/>
			</Box>
		);
	}
}

export default withTranslation(TwoFaChoice);
